import React, { useEffect, useMemo, useState } from 'react';
import * as R from 'ramda';

import {
  ClickAwayListener,
  DatePicker,
  DateTimePicker,
  Select,
} from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  dateOptions,
  DateRange,
  DateRangePresetOption,
  getDateRangeLabel,
  getDatesFromSelection,
  isCustomDatePreset,
} from '@atom/utilities/workOrdersDateFilterUtilities';

const { MenuItem } = Select;

const styles = {
  separator: {
    margin: '0 1rem',
    paddingBottom: '0.5rem',
  },
  dateContainer: {
    padding: '1rem',
    background: colors.neutral.white,
    borderRadius: '4px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.3)',
  },
};

interface Props {
  label?: string;
  disabled?: boolean;
  startDateValue?: Date;
  handleStartChange: (val: Date) => void;
  endDateValue?: Date;
  handleEndChange: (val: Date) => void;
  presetSelected?: string;
  handleNoDueDateSelect?: (selected: boolean) => void;
  handleSelectDatePreset?: (selected: DateRangePresetOption) => void;
}

const InputDateRange = ({
  label = '',
  disabled = false,
  startDateValue,
  endDateValue,
  presetSelected,
  handleStartChange,
  handleEndChange,
  handleNoDueDateSelect,
  handleSelectDatePreset,
}: Props) => {
  const [showDatePickers, setShowDatePickers] = useState(false);
  const [customLabel, setCustomLabel] = useState<string>();
  const [customPresetOpen, setCustomPresetOpen] = useState<DateRange>(null);

  const options = useMemo(() => {
    const localOptions = R.clone(dateOptions);
    return handleNoDueDateSelect
      ? localOptions
      : localOptions.filter(option => option.value !== DateRange.NO_DATE);
  }, [handleNoDueDateSelect]);

  const getSelectedValue = (): DateRangePresetOption => {
    if (!R.isNil(presetSelected) && !isCustomDatePreset(presetSelected)) {
      return dateOptions.find(option => option.value === presetSelected);
    }

    if (R.isNil(startDateValue) && R.isNil(endDateValue)) {
      return dateOptions.find(option => option.value === DateRange.ANYTIME);
    }

    const customValue = isCustomDatePreset(presetSelected)
      ? presetSelected
      : DateRange.CUSTOM_RANGE;
    const newCustomLabel = getDateRangeLabel(
      startDateValue,
      endDateValue,
      customValue,
    );
    setCustomLabel(newCustomLabel);
    const customOption = options.find(option => option.value === customValue);
    return customOption;
  };

  const [selected, setSelected] =
    useState<DateRangePresetOption>(getSelectedValue);

  useEffect(() => {
    setCustomLabel(null);
    setSelected(getSelectedValue());
  }, [presetSelected, startDateValue, endDateValue]);

  const handleClickOutside = () => {
    if (startDateValue === null && endDateValue === null) {
      setSelected(
        dateOptions.find(option => option.value === DateRange.ANYTIME),
      );
    } else {
      const customValue = customPresetOpen || DateRange.CUSTOM_RANGE;
      const newCustomLabel = getDateRangeLabel(
        startDateValue,
        endDateValue,
        customValue,
      );
      setCustomLabel(newCustomLabel);
      const customOption = options.find(option => option.value === customValue);
      setSelected(customOption);
    }
    setShowDatePickers(false);
  };

  const handleSelect = (event: React.ChangeEvent<any>) => {
    const selectedOption = options.find(
      option => option.value === event.target.value,
    );
    setSelected(selectedOption);

    // reset custom range label from custom dates on any selection
    setCustomLabel(null);

    if (handleNoDueDateSelect) {
      handleNoDueDateSelect(event.target.value === DateRange.NO_DATE);
    }

    handleSelectDatePreset(selectedOption);

    const [startDate, endDate] = getDatesFromSelection(event.target.value);
    handleStartChange(startDate);
    handleEndChange(endDate);
  };

  // handle case where user clicks on customized date label
  const handleSelectedCustomRangeClick = clickEvent => {
    const clickedValue = clickEvent?.target?.dataset?.value;
    if (isCustomDatePreset(clickedValue)) {
      setCustomPresetOpen(clickedValue as DateRange);
      setShowDatePickers(true);
    }
  };

  const isDateTimeRange = customPresetOpen === DateRange.CUSTOM_DATE_TIME_RANGE;

  return (
    <>
      {showDatePickers ? (
        <ClickAwayListener onClickAway={handleClickOutside}>
          <div style={styles.dateContainer}>
            {isDateTimeRange ? (
              <>
                <DateTimePicker
                  value={startDateValue}
                  onChange={handleStartChange}
                  label={label}
                  disabled={disabled}
                  data-cy={`dateTimeRangePickerStart-${label}`}
                />
                <span style={styles.separator}>to</span>
                <DateTimePicker
                  value={endDateValue}
                  onChange={handleEndChange}
                  disabled={disabled}
                  data-cy={`dateTimeRangePickerEnd-${label}`}
                />
              </>
            ) : (
              <>
                <DatePicker
                  value={startDateValue}
                  onChange={handleStartChange}
                  label={label}
                  disabled={disabled}
                  data-cy={`dateRangePickerStart-${label}`}
                />
                <span style={styles.separator}>to</span>
                <DatePicker
                  value={endDateValue}
                  onChange={handleEndChange}
                  disabled={disabled}
                  data-cy={`dateRangePickerEnd-${label}`}
                />
              </>
            )}
          </div>
        </ClickAwayListener>
      ) : (
        <Select
          label={label}
          value={selected?.value}
          onChange={handleSelect}
          disabled={disabled}
          data-cy={`dateRangeSelect-${label}`}
        >
          {options.map((option, idx) => (
            <MenuItem
              onClick={handleSelectedCustomRangeClick}
              key={idx}
              value={option.value}
            >
              {isCustomDatePreset(option.value) &&
              option.value === selected?.value
                ? customLabel || option.label
                : option.label}
            </MenuItem>
          ))}
        </Select>
      )}
    </>
  );
};

export default InputDateRange;
